import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { MDBBtn, MDBIcon, MDBTable } from "mdbreact";
import { Services } from "../../../../../../services/fakeDb";
import { GenericProducts } from "../../../../../../constants";
import { axioKit } from "../../../../../../services/utilities";
import { isUsableIn } from "../../../../manager/commerce/merchandise/productGenerics/phases";
import {
  UTILIZATION_URL,
  UTILIZATION_DEPARTMENT,
  UTILIZATION_PHASES,
  toItems,
} from "../../../../laboratory/catalog/preAnalytical";
import {
  autoSelect,
  isComplete,
} from "../../../../laboratory/catalog/brandedProducts/select";
import SupplyPicker from "../../../../laboratory/catalog/preAnalytical/setup/supplyPicker";
import SupplyRow from "../../../../laboratory/catalog/preAnalytical/setup/supplyRow";
import {
  STATUS,
  newBrandsOf,
  signature,
  statusOf,
} from "../../../../laboratory/catalog/preAnalytical/setup/status";

/**
 * Disposables of one service per phase (PRE / ANA / POST), same tagging as
 * Consumables: generic -> default brand/variant -> units, auto-consumed upon dispensing.
 */
export default function Disposables({ serviceId }) {
  const { token, activePlatform } = useSelector(({ auth }) => auth),
    [phase, setPhase] = useState("preAnalytical"),
    [products, setProducts] = useState([]),
    [utilization, setUtilization] = useState(null),
    [items, setItems] = useState([]),
    [isLoading, setIsLoading] = useState(false),
    { addToast } = useToasts();

  const branch = activePlatform?.branchId;
  const service = Services.find(serviceId);
  const department = UTILIZATION_DEPARTMENT[service?.department] || "";

  const notify = useCallback(
    (message, appearance = "error") =>
      addToast(message, { appearance, autoDismiss: true }),
    [addToast],
  );

  useEffect(() => {
    if (!branch || !token || !department) return;
    setIsLoading(true);
    Promise.all([
      axioKit.universal("commerce/merchandise/products/browse", token, {
        branch,
      }),
      axioKit.universal(`${UTILIZATION_URL}/browse`, token, {
        branch,
        department,
      }),
    ])
      .then(([_products, _utilizations]) => {
        setProducts(_products?.payload || []);
        setUtilization(
          (_utilizations?.payload || []).find(
            (u) => u.serviceId === Number(serviceId),
          ) || null,
        );
      })
      .catch((error) => notify(error.message))
      .finally(() => setIsLoading(false));
  }, [branch, token, department, serviceId, notify]);

  // what is saved for the current phase
  const stored = useMemo(
    () =>
      (utilization?.phases?.[phase] || []).filter(({ pgid }) =>
        GenericProducts.find(pgid),
      ),
    [utilization, phase],
  );
  const saved = useMemo(() => toItems(stored, products), [stored, products]);

  useEffect(() => setItems(saved), [saved]);

  const taggedAt = utilization?.updatedAt;
  const isDirty = signature(items) !== signature(stored);
  const incomplete = items.filter((item) => !isComplete(item));
  const status = STATUS[statusOf(items, products, taggedAt)];
  const hasNewBrands = items.some(
    (item) => newBrandsOf(products, item, taggedAt).length > 0,
  );

  // per-test generics of this phase (or general) not yet tagged
  const options = useMemo(
    () =>
      GenericProducts.perTest.filter(
        ({ id, phases }) =>
          isUsableIn(phases, phase) &&
          !items.some(({ generic }) => generic.id === id),
      ),
    [items, phase],
  );

  // tagged count per phase, from what is saved
  const counts = useMemo(
    () =>
      Object.keys(UTILIZATION_PHASES).reduce((result, key) => {
        result[key] = (utilization?.phases?.[key] || []).filter(({ pgid }) =>
          GenericProducts.find(pgid),
        ).length;
        return result;
      }, {}),
    [utilization],
  );

  const handlePhase = (key) => {
    if (key === phase) return;
    if (
      isDirty &&
      !window.confirm(
        "You have unsaved disposables on this phase. Discard them?",
      )
    )
      return;
    setPhase(key);
  };

  const handleAdd = (generic) =>
    setItems((prev) => [
      ...prev,
      { generic, ...autoSelect(products, generic.id), quantity: 1 },
    ]);

  const handleChange = (index, changes) =>
    setItems((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...changes } : row)),
    );

  const handleRemove = (index) =>
    setItems((prev) => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const { success, payload } = await axioKit.save(
        UTILIZATION_URL,
        {
          branch,
          serviceId: Number(serviceId),
          department,
          phase,
          items: items.map(({ generic, product, variant, quantity }) => ({
            pgid: generic.id,
            product: product?._id,
            variant: variant?._id,
            quantity,
          })),
        },
        token,
      );
      setUtilization(payload);
      notify(success, "success");
    } catch (error) {
      notify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (!department)
    return (
      <p className="text-center text-muted small my-3">
        Disposables are tagged for laboratory and radiology services only.
      </p>
    );

  return (
    <div>
      <div
        className="d-flex flex-wrap justify-content-between align-items-center mb-2"
        style={{ gap: 8 }}
      >
        <div className="btn-group" role="group" aria-label="Phase">
          {Object.entries(UTILIZATION_PHASES).map(([key, p]) => (
            <button
              key={key}
              type="button"
              className={`btn btn-sm m-0 z-depth-0 ${
                key === phase ? "btn-primary" : "btn-outline-primary"
              }`}
              style={{ textTransform: "none" }}
              title={p.label}
              disabled={isLoading}
              onClick={() => handlePhase(key)}
            >
              <MDBIcon icon={p.icon} className="mr-1" />
              {p.abbr}
              {counts[key] > 0 && (
                <span className="badge badge-light ml-1">{counts[key]}</span>
              )}
            </button>
          ))}
        </div>
        <div className="text-right">
          <span
            className="small font-weight-bold"
            style={{ color: status.color }}
            title={status.hint}
          >
            <MDBIcon icon={status.icon} className="mr-1" />
            {status.label}
          </span>
          {isDirty && (
            <small className="d-block text-warning">
              <MDBIcon icon="pen" className="mr-1" />
              Unsaved changes
            </small>
          )}
        </div>
      </div>

      <SupplyPicker
        options={options}
        products={products}
        onAdd={handleAdd}
        disabled={isLoading}
      />

      <MDBTable small hover responsive className="mt-3 mb-2 text-left">
        <thead>
          <tr>
            <th>Generic supply</th>
            <th style={{ width: "40%" }}>Default brand / variant</th>
            <th
              style={{ width: 130 }}
              title="Default units, can be increased upon dispensing"
            >
              Units
            </th>
            <th style={{ width: 30 }} />
            <th style={{ width: 30 }} />
          </tr>
        </thead>
        <tbody>
          {items.length ? (
            items.map((item, index) => (
              <SupplyRow
                key={item.generic.id}
                item={item}
                products={products}
                taggedAt={taggedAt}
                onChange={(changes) => handleChange(index, changes)}
                onRemove={() => handleRemove(index)}
                disabled={isLoading}
              />
            ))
          ) : (
            <tr>
              <td colSpan={5} className="text-center text-muted py-3">
                {isLoading
                  ? "Loading..."
                  : `No ${UTILIZATION_PHASES[phase].title.toLowerCase()} disposables tagged yet. Search above to add one.`}
              </td>
            </tr>
          )}
        </tbody>
      </MDBTable>

      {incomplete.length > 0 && (
        <div className="alert alert-warning py-1 px-2 small mb-2 text-left">
          <MDBIcon icon="exclamation-triangle" className="mr-1" />
          No default brand/variant yet for{" "}
          <b>
            {incomplete.map(({ generic }) => generic.abbreviation).join(", ")}
          </b>
          . They are saved as tagged, but cannot be auto-consumed until a brand
          is registered in Products.
        </div>
      )}

      <div className="d-flex justify-content-end">
        <MDBBtn
          size="sm"
          color="light"
          onClick={() => setItems(saved)}
          disabled={isLoading || !isDirty}
        >
          Reset
        </MDBBtn>
        <MDBBtn
          size="sm"
          color="primary"
          className="mr-0"
          onClick={handleSave}
          disabled={isLoading || (!isDirty && !hasNewBrands)}
        >
          <MDBIcon icon="save" className="mr-1" />
          {!isDirty && hasNewBrands ? "Keep defaults" : "Save"}
        </MDBBtn>
      </div>
    </div>
  );
}
